import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartFunnelSeatCountItemBlockConfig } from '@front-platform/content-builder';
import { cartFunnelSeatCountItemBlockResolveConfig } from '@front-platform/content-builder';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { DoodCartFunnelValueTarget } from '@store/cart-funnel/cart-funnel.model';
import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';
import { TranslationField } from '@shared/interfaces/translation-field.interface';
import { TranslateService } from '@ngx-translate/core';
import { TranslateFieldPipe } from '@shared/pipes/translate-field/translate-field.pipe';
export interface CartFunnelSeatCountItemBlockRuntimeConfig {
  'required'?: boolean;
  'block_id'?: string;
  'form_id'?: string;
}

export type CartFunnelSeatCountItemBlockConfigInput = Partial<
  Omit<CartFunnelSeatCountItemBlockConfig, keyof CartFunnelSeatCountItemBlockRuntimeConfig> &
    CartFunnelSeatCountItemBlockRuntimeConfig
>;
export type CartFunnelSeatCountItemBlockResolvedConfig = Omit<
  CartFunnelSeatCountItemBlockConfig,
  keyof CartFunnelSeatCountItemBlockRuntimeConfig
> &
  Partial<CartFunnelSeatCountItemBlockRuntimeConfig>;
export interface CartFunnelSeatCountItemBlockContract extends ConfigComponentContract<
  CartFunnelSeatCountItemBlockConfigInput,
  CartFunnelSeatCountItemBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-funnel-seat-count-item-block',
  templateUrl: './cart-funnel-seat-count-item-block.component.html',
  standalone: true,
  imports: [CommonModule, TranslateModule, TranslateFieldPipe],
})
export class CartFunnelSeatCountItemBlockComponent
  implements OnInit, CartFunnelSeatCountItemBlockContract
{
  readonly config = input<CartFunnelSeatCountItemBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartFunnelSeatCountItemBlockConfig,
    CartFunnelSeatCountItemBlockRuntimeConfig
  >(cartFunnelSeatCountItemBlockResolveConfig, () => ({ config: this.config() }));
  private readonly cartFunnelStoreSelector = inject(CartFunnelStoreSelector);
  private readonly cartFunnelStoreDispatcher = inject(CartFunnelStoreDispatcher);
  private readonly translateService = inject(TranslateService);

  readonly title = computed(
    () => this.resolvedConfig()['title'] as unknown as TranslationField | undefined,
  );
  readonly placeholder = computed(
    () => this.resolvedConfig()['placeholder'] as unknown as TranslationField | undefined,
  );
  readonly required = computed(() => {
    const value = this.resolvedConfig()['required'];
    return value === undefined ? true : (value as unknown as boolean);
  });
  readonly blockId = computed(() => this.resolvedConfig()['block_id'] as unknown as string);
  readonly formId = computed(() => this.resolvedConfig()['form_id'] as unknown as string);

  protected readonly keyboardInput = signal<string>('');
  protected readonly keypadLayout: string[] = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '<',
    '0',
    'EFFACER',
  ];

  protected readonly value = computed(() =>
    signal<string | null>(this.cartFunnelStoreSelector.selectSeatCount()?.toString() ?? null),
  );

  ngOnInit(): void {
    if (!this.blockId()) {
      console.error('[CartFunnelSeatCountItemBlockComponent] blockId is missing', {
        blockId: this.blockId(),
      });
      return;
    }

    // Use blockId as formId if formId is not provided (when used as a standalone step)
    const formId = this.formId() || this.blockId();

    this.cartFunnelStoreDispatcher.addValue({
      id: this.blockId(),
      title: this.title()?.[this.translateService.currentLang] ?? 'cart-funnel.seatCount.title',
      displayTitle: true,
      formId: formId,
      required: this.required(),
      type: 'number',
      target: DoodCartFunnelValueTarget.SeatCount,
    });

    // Initialize from store value
    const currentSeatCount = this.cartFunnelStoreSelector.selectSeatCount();
    if (currentSeatCount !== null && currentSeatCount !== undefined) {
      this.keyboardInput.set(currentSeatCount.toString());
    }
  }

  // Handle numeric keyboard press for kiosk/desktop
  handleKeyPress(key: string): void {
    if (key === 'EFFACER') {
      this.clearKeyboardInput();
    } else if (key === '<') {
      this.keyboardInput.set(this.keyboardInput().slice(0, -1));
    } else {
      this.keyboardInput.set(this.keyboardInput() + key);
    }

    // Update value when input changes
    const value = this.keyboardInput();
    if (value.trim() !== '') {
      const parsed = Number(value);
      if (!isNaN(parsed) && parsed > 0) {
        this.updateValue(parsed);
      }
    } else {
      this.updateValue(undefined);
    }
  }

  clearKeyboardInput(): void {
    this.keyboardInput.set('');
    this.updateValue(undefined);
  }

  private updateValue(numValue: number | undefined): void {
    this.cartFunnelStoreDispatcher.updateValue({
      id: this.blockId(),
      changes: {
        value: numValue,
      },
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartFunnelSeatCountItemBlockComponent, {
  'keyAliases': {},
});
